import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import {
  WorkerBatchDetail,
  WorkerBatchGroup,
  WorkerBatchSample,
  WorkerBatchStat,
  WorkerFeedItem,
  WorkerIssue,
  WorkerStatus,
  WorkerSuggestedAction,
  WorkersIssuesResponse,
  WorkersStatusResponse
} from '@core/models/models';

type FeedFilter = 'all' | 'errors' | string;

interface WorkerBatchView {
  processed: number;
  stats: WorkerBatchStat[];
  groups: WorkerBatchGroup[];
  hasSamples: boolean;
}

const BATCH_WORKERS = new Set([
  'product-images',
  'storefront-names',
  'open-food-facts',
  'rami-aisle-codes',
  'shufersal-aisle-codes'
]);


@Component({
  selector: 'app-workers-admin',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './workers-admin.component.html',
  styleUrl: './workers-admin.component.css'
})
export class WorkersAdminComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly pollMs = 2500;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler = () => this.onVisibilityChange();

  status: WorkersStatusResponse | null = null;
  issues: WorkersIssuesResponse | null = null;
  feedItems: WorkerFeedItem[] = [];
  sinceId = 0;
  sinceEventId = 0;
  feedFilter: FeedFilter = 'all';
  loading = true;
  errorMessage = '';
  actionNote = '';
  busyWorkerId: string | null = null;
  busyIssueCode: string | null = null;
  expandedFeedId: string | null = null;
  private readonly collapsedRecentKeys = new Set<string>();
  private readonly expandedRecentKeys = new Set<string>();
  private readonly batchCache = new WeakMap<WorkerFeedItem, WorkerBatchView | null>();

  async ngOnInit(): Promise<void> {
    document.addEventListener('visibilitychange', this.visibilityHandler);
    await this.refresh(true);
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  get filteredFeed(): WorkerFeedItem[] {
    if (this.feedFilter === 'all') {
      return this.feedItems;
    }
    if (this.feedFilter === 'errors') {
      return this.feedItems.filter(
        (item) => item.severity === 'error' || item.severity === 'warning'
      );
    }
    return this.feedItems.filter((item) => item.workerId === this.feedFilter);
  }

  get healthy(): boolean {
    const issueCount = this.issues?.issues?.length ?? 0;
    const errorWorkers =
      this.status?.workers.filter((w) => w.state === 'error').length ?? 0;
    return issueCount === 0 && errorWorkers === 0 && this.feedItems.length >= 0;
  }

  async refresh(initial = false): Promise<void> {
    if (document.hidden && !initial) {
      return;
    }

    try {
      const [status, feed, issues] = await Promise.all([
        firstValueFrom(this.api.getWorkersStatus()),
        firstValueFrom(this.api.getWorkersFeed(this.sinceId, this.sinceEventId)),
        firstValueFrom(this.api.getWorkersIssues())
      ]);

      this.status = status;
      this.issues = issues;
      this.mergeFeed(feed.items, initial);
      this.sinceId = Math.max(this.sinceId, feed.nextSinceId);
      this.sinceEventId = Math.max(this.sinceEventId, feed.nextEventSinceId);
      this.errorMessage = '';
    } catch {
      this.errorMessage = 'Could not load worker status. Retrying…';
    } finally {
      this.loading = false;
    }
  }

  setFeedFilter(filter: FeedFilter): void {
    this.feedFilter = filter;
  }

  toggleFeedDetail(item: WorkerFeedItem): void {
    const key = this.feedKey(item);
    this.expandedFeedId = this.expandedFeedId === key ? null : key;
  }

  feedKey(item: WorkerFeedItem): string {
    return `${item.source}:${item.id}`;
  }

  relativeTime(iso: string | null | undefined): string {
    if (!iso) {
      return 'never';
    }
    const at = new Date(iso).getTime();
    if (Number.isNaN(at)) {
      return '—';
    }
    const seconds = Math.round((Date.now() - at) / 1000);
    if (seconds < 5) {
      return 'just now';
    }
    if (seconds < 60) {
      return `${seconds}s ago`;
    }
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    return new Date(iso).toLocaleString();
  }

  shortDateTime(iso: string | null | undefined): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    if (sameDay) {
      return time;
    }
    const day = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `${day} ${time}`;
  }

  showRecent(worker: WorkerStatus): boolean {
    return worker.id !== 'ingestion';
  }

  workerBatch(item: WorkerFeedItem): WorkerBatchView | null {
    if (this.batchCache.has(item)) {
      return this.batchCache.get(item) ?? null;
    }
    const parsed = this.parseWorkerBatchDetail(item);
    this.batchCache.set(item, parsed);
    return parsed;
  }

  isBatchRecentExpanded(worker: WorkerStatus, item: WorkerFeedItem, index: number): boolean {
    const key = this.recentKey(worker, item);
    if (this.collapsedRecentKeys.has(key)) {
      return false;
    }
    if (this.expandedRecentKeys.has(key)) {
      return true;
    }
    return index === 0;
  }

  toggleBatchRecent(worker: WorkerStatus, item: WorkerFeedItem, index: number): void {
    const key = this.recentKey(worker, item);
    const open = this.isBatchRecentExpanded(worker, item, index);
    if (open) {
      this.expandedRecentKeys.delete(key);
      this.collapsedRecentKeys.add(key);
    } else {
      this.collapsedRecentKeys.delete(key);
      this.expandedRecentKeys.add(key);
    }
  }

  sampleLabel(sample: WorkerBatchSample): string {
    const name = sample.name?.trim();
    if (name) {
      return name;
    }
    return sample.id;
  }

  sampleMeta(sample: WorkerBatchSample): string {
    const parts: string[] = [];
    if (sample.name?.trim()) {
      parts.push(sample.id);
    }
    if (sample.meta?.trim()) {
      parts.push(sample.meta.trim().replace(/_/g, ' '));
    }
    return parts.join(' · ');
  }

  sampleUrl(sample: WorkerBatchSample): string | null {
    const url = sample.url?.trim();
    return url && /^https?:\/\//i.test(url) ? url : null;
  }

  moreCount(total: number, shown: number): number {
    return Math.max(0, total - shown);
  }

  trackSample(_: number, sample: WorkerBatchSample): string {
    return sample.id;
  }

  trackStat(_: number, stat: WorkerBatchStat): string {
    return stat.key;
  }

  trackGroup(_: number, group: WorkerBatchGroup): string {
    return group.key;
  }

  stateLabel(state: string): string {
    return state;
  }

  private recentKey(worker: WorkerStatus, item: WorkerFeedItem): string {
    return `${worker.id}:${item.source}:${item.id}`;
  }

  private parseWorkerBatchDetail(item: WorkerFeedItem): WorkerBatchView | null {
    if (!item.detail) {
      return null;
    }
    try {
      const raw = JSON.parse(item.detail) as Record<string, unknown>;
      if (raw['kind'] === 'worker-batch') {
        return this.fromWorkerBatch(raw as unknown as Partial<WorkerBatchDetail>);
      }
      if (raw['kind'] === 'product-images-batch') {
        return this.fromLegacyImageBatch(raw);
      }
      if (BATCH_WORKERS.has(item.workerId)) {
        return this.fromApiBatchResult(item.workerId, raw);
      }
      return null;
    } catch {
      return null;
    }
  }

  private fromWorkerBatch(raw: Partial<WorkerBatchDetail>): WorkerBatchView | null {
    const stats = Array.isArray(raw.stats) ? raw.stats : [];
    const groups = Array.isArray(raw.groups) ? raw.groups : [];
    if (stats.length === 0 && groups.length === 0) {
      return null;
    }
    return {
      processed: Number(raw.processed) || 0,
      stats,
      groups,
      hasSamples: groups.some((group) => (group.samples?.length ?? 0) > 0)
    };
  }

  private fromLegacyImageBatch(raw: Record<string, unknown>): WorkerBatchView {
    const found = Number(raw['found']) || 0;
    const notFound = Number(raw['notFound']) || 0;
    const errors = Number(raw['errors']) || 0;
    const processed = Number(raw['processed']) || 0;
    const foundSamples = this.normalizeSamples(raw['foundSamples']);
    const notFoundSamples = this.normalizeSamples(raw['notFoundSamples']);
    const errorSamples = this.normalizeSamples(raw['errorSamples']);
    return this.buildFoundMissedView(
      processed,
      found,
      notFound,
      errors,
      foundSamples,
      notFoundSamples,
      errorSamples
    );
  }

  private fromApiBatchResult(
    workerId: string,
    raw: Record<string, unknown>
  ): WorkerBatchView | null {
    const processed = Number(raw['processed'] ?? raw['requested']) || 0;
    if (!processed && !raw['found'] && !raw['filled']) {
      return null;
    }

    if (workerId === 'open-food-facts') {
      return this.buildFoundMissedView(
        processed,
        Number(raw['found']) || 0,
        Number(raw['notFound']) || 0,
        Number(raw['failed'] ?? raw['errors']) || 0,
        this.normalizeSamples(raw['foundSamples']),
        this.normalizeSamples(raw['notFoundSamples']),
        this.normalizeSamples(raw['errorSamples']),
        'failed'
      );
    }

    if (workerId === 'rami-aisle-codes' || workerId === 'shufersal-aisle-codes') {
      return this.buildFoundMissedView(
        processed,
        Number(raw['filled'] ?? raw['found']) || 0,
        Number(raw['notFound']) || 0,
        Number(raw['errors']) || 0,
        this.normalizeSamples(raw['filledSamples'] ?? raw['foundSamples']),
        this.normalizeSamples(raw['notFoundSamples']),
        this.normalizeSamples(raw['errorSamples']),
        'errors',
        'Filled',
        'filled'
      );
    }

    return this.buildFoundMissedView(
      processed,
      Number(raw['found']) || 0,
      Number(raw['notFound']) || 0,
      Number(raw['errors']) || 0,
      this.normalizeSamples(raw['foundSamples']),
      this.normalizeSamples(raw['notFoundSamples']),
      this.normalizeSamples(raw['errorSamples'])
    );
  }

  private buildFoundMissedView(
    processed: number,
    found: number,
    notFound: number,
    errors: number,
    foundSamples: WorkerBatchSample[],
    notFoundSamples: WorkerBatchSample[],
    errorSamples: WorkerBatchSample[],
    errorsChip = 'errors',
    foundLabel = 'Found',
    foundChip = 'found'
  ): WorkerBatchView {
    const stats: WorkerBatchStat[] = [
      { key: 'processed', label: 'done', count: processed, tone: 'neutral' },
      { key: 'found', label: foundChip, count: found, tone: 'found' },
      { key: 'notFound', label: 'missed', count: notFound, tone: 'missed' }
    ];
    if (errors > 0) {
      stats.push({ key: 'errors', label: errorsChip, count: errors, tone: 'errors' });
    }

    const groups: WorkerBatchGroup[] = [];
    if (foundSamples.length > 0) {
      groups.push({
        key: 'found',
        label: foundLabel,
        tone: 'found',
        total: found,
        samples: foundSamples
      });
    }
    if (notFoundSamples.length > 0) {
      groups.push({
        key: 'notFound',
        label: 'Not found',
        tone: 'missed',
        total: notFound,
        samples: notFoundSamples
      });
    }
    if (errorSamples.length > 0) {
      groups.push({
        key: 'errors',
        label: errorsChip === 'failed' ? 'Failed' : 'Errors',
        tone: 'errors',
        total: errors,
        samples: errorSamples
      });
    }

    return {
      processed,
      stats,
      groups,
      hasSamples: groups.length > 0
    };
  }

  private normalizeSamples(value: unknown): WorkerBatchSample[] {
    if (!Array.isArray(value)) {
      return [];
    }
    const samples: WorkerBatchSample[] = [];
    for (const entry of value) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const raw = entry as Record<string, unknown>;
      const id = String(raw['id'] ?? raw['barcode'] ?? '').trim();
      if (!id) {
        continue;
      }
      const metaParts = [raw['meta'], raw['source'], raw['error']]
        .map((part) => (typeof part === 'string' ? part.trim() : ''))
        .filter(Boolean);
      samples.push({
        id,
        name: typeof raw['name'] === 'string' ? raw['name'] : null,
        meta: metaParts.length > 0 ? metaParts.join(' · ') : null,
        url: typeof raw['url'] === 'string' ? raw['url'] : null
      });
      if (samples.length >= 3) {
        break;
      }
    }
    return samples;
  }

  async runAction(
    action: WorkerSuggestedAction | null | undefined,
    workerId?: string,
    issueCode?: string
  ): Promise<void> {
    if (!action) {
      return;
    }
    this.busyWorkerId = workerId ?? null;
    this.busyIssueCode = issueCode ?? null;
    this.actionNote = '';
    try {
      const result = await firstValueFrom(this.api.runWorkerAction(action));
      this.actionNote = `${action.label} started.`;
      this.prependLocalFeed({
        id: Date.now(),
        at: new Date().toISOString(),
        workerId: workerId ?? 'manual',
        severity: 'info',
        message: this.formatActionMessage(action.label, result),
        detail: this.formatActionDetail(workerId, result),
        source: 'local'
      });
      await this.refresh(true);
    } catch {
      this.actionNote = `${action.label} failed.`;
      this.prependLocalFeed({
        id: Date.now(),
        at: new Date().toISOString(),
        workerId: workerId ?? 'manual',
        severity: 'error',
        message: `${action.label} failed.`,
        source: 'local'
      });
    } finally {
      this.busyWorkerId = null;
      this.busyIssueCode = null;
    }
  }

  trackWorker(_: number, worker: WorkerStatus): string {
    return worker.id;
  }

  trackFeed(_: number, item: WorkerFeedItem): string {
    return this.feedKey(item);
  }

  trackIssue(_: number, issue: WorkerIssue): string {
    return `${issue.kind}:${issue.code}:${issue.title}`;
  }

  private mergeFeed(incoming: WorkerFeedItem[], replace: boolean): void {
    if (replace && this.sinceId === 0 && this.sinceEventId === 0) {
      this.feedItems = incoming;
      return;
    }

    const seen = new Set(this.feedItems.map((item) => this.feedKey(item)));
    const fresh = incoming.filter((item) => !seen.has(this.feedKey(item)));
    if (fresh.length === 0) {
      return;
    }
    this.feedItems = [...fresh, ...this.feedItems].slice(0, 200);
  }

  private prependLocalFeed(item: WorkerFeedItem): void {
    this.feedItems = [item, ...this.feedItems].slice(0, 200);
  }

  private formatActionMessage(label: string, result: unknown): string {
    if (!result || typeof result !== 'object') {
      return `${label} triggered from dashboard.`;
    }
    const raw = result as Record<string, unknown>;
    const processed = Number(raw['processed'] ?? raw['requested']);
    if (!Number.isFinite(processed)) {
      return `${label} triggered from dashboard.`;
    }
    const found = Number(raw['found'] ?? raw['filled']) || 0;
    const errors = Number(raw['errors'] ?? raw['failed']) || 0;
    const foundKey = 'filled' in raw ? 'filled' : 'found';
    const errorKey = 'failed' in raw ? 'failed' : 'errors';
    return `${label}: processed=${processed}, ${foundKey}=${found}, ${errorKey}=${errors}.`;
  }

  private formatActionDetail(workerId: string | undefined, result: unknown): string | null {
    if (workerId && BATCH_WORKERS.has(workerId) && result && typeof result === 'object') {
      const view = this.fromApiBatchResult(workerId, result as Record<string, unknown>);
      if (view) {
        return JSON.stringify({
          kind: 'worker-batch',
          processed: view.processed,
          stats: view.stats,
          groups: view.groups
        });
      }
    }
    if (typeof result === 'object' && result !== null) {
      return JSON.stringify(result).slice(0, 240);
    }
    return null;
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollTimer = setInterval(() => {
      void this.refresh(false);
    }, this.pollMs);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private onVisibilityChange(): void {
    if (document.hidden) {
      this.stopPolling();
    } else {
      void this.refresh(false);
      this.startPolling();
    }
  }
}
